// BoardDetailPage — /board/:id (공지처럼 별도 페이지로 보기)
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";
import {
  Avatar, CommentItem, PostModal, catColor, fmtDate,
  type BoardCategory, type PostDetail,
} from "./BoardPage";

export default function BoardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [cats,    setCats]    = useState<BoardCategory[]>([]);
  const [post,    setPost]    = useState<PostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [showEdit, setShowEdit] = useState(false);

  const authUser     = useAuth(s => s.user);
  const authManifest = useAuth(s => s.manifest);
  const me: number | null = authUser?.id ?? null;
  const meRoles: string[] = authManifest?.roles ?? authUser?.roles ?? [];

  const load = useCallback(() => {
    if (!id) return;
    api.get<PostDetail>(`/forum/posts/${id}`)
      .then(r => setPost(r.data))
      .catch(() => { toast.error("게시글을 찾을 수 없습니다"); navigate("/board", { replace: true }); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    api.get<BoardCategory[]>("/forum/categories").then(r => setCats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { setLoading(true); load(); }, [load]);

  const color = post ? catColor(cats, post.category) : "#6366f1";
  const canManage = !!post && (me === post.author_id || meRoles.includes("SUPER_ADMIN"));

  const deletePost = async () => {
    if (!post || !window.confirm("게시글을 삭제하시겠습니까?")) return;
    try { await api.delete(`/forum/posts/${post.id}`); toast.success("삭제됨"); navigate("/board"); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const addComment = async () => {
    if (!post || !commentText.trim()) return;
    try {
      await api.post(`/forum/posts/${post.id}/comments`, { body_md: commentText, parent_id: replyTo });
      setCommentText(""); setReplyTo(null); load();
    } catch { toast.error("댓글 등록 실패"); }
  };
  const deleteComment = async (cid: number) => {
    if (!post || !window.confirm("댓글을 삭제하시겠습니까?")) return;
    try { await api.delete(`/forum/posts/${post.id}/comments/${cid}`); load(); }
    catch { toast.error("댓글 삭제 실패"); }
  };
  const react = async (emoji: string) => {
    if (!post) return;
    try { await api.post(`/forum/posts/${post.id}/react`, { emoji }); load(); } catch {}
  };

  if (loading) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--text-3)", fontSize: 14 }}>불러오는 중…</div>;
  }
  if (!post) return null;

  const catLabel = cats.find(c => c.code === post.category)?.label ?? post.category;

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 28px 48px", boxSizing: "border-box" }}>
      {/* Breadcrumb / Back */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "20px 0" }}>
        <button onClick={() => navigate("/board")}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, color: "var(--text-2)" }}
          onMouseEnter={e => (e.currentTarget.style.background = "var(--border)")}
          onMouseLeave={e => (e.currentTarget.style.background = "none")}>
          ← 목록으로
        </button>
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>게시판</span>
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>›</span>
        <span style={{ fontSize: 12, color: "var(--text-3)", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: 320 }}>{post.title}</span>
      </div>

      {/* Article */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <div style={{ height: 4, background: color }} />

        {/* Header */}
        <div style={{ padding: "24px 32px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: color + "22", color }}>{catLabel}</span>
            {post.is_pinned && <span style={{ fontSize: 12, color, fontWeight: 700 }}>📌 공지</span>}
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, lineHeight: 1.4, color: "var(--text)", flex: 1 }}>{post.title}</h1>
            <div style={{ display: "flex", gap: 6, flexShrink: 0, marginTop: 2 }}>
              {canManage && (
                <>
                  <button className="t-btn" style={{ fontSize: 13, padding: "6px 14px" }} onClick={() => setShowEdit(true)}>수정</button>
                  <button className="t-btn" style={{ fontSize: 13, padding: "6px 14px", color: "var(--danger)" }} onClick={deletePost}>삭제</button>
                </>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 12, fontSize: 13, color: "var(--text-3)", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Avatar name={post.author_name} id={post.author_id} size={18} /> {post.author_name}
            </span>
            <span>{fmtDate(post.created_at)}</span>
            <span>👁 {post.views}</span>
            <span>👍 {post.like_count}</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "28px 32px" }}>
          <div style={{ fontSize: 15, lineHeight: 1.9, color: "var(--text)", whiteSpace: "pre-wrap", minHeight: 80 }}>
            {post.body_md || <span style={{ color: "var(--text-3)" }}>(내용 없음)</span>}
          </div>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 20 }}>
              {post.tags.map(t => (
                <span key={t} style={{ fontSize: 12, padding: "3px 12px", borderRadius: 20, background: color + "18", color }}>#{t}</span>
              ))}
            </div>
          )}

          {/* Reactions */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 20 }}>
            {["👍","❤️","😄","🔥","🎉"].map(em => (
              <button key={em} className="t-btn" style={{ fontSize: 17, padding: "5px 12px", lineHeight: 1, border: "1px solid var(--border)" }} onClick={() => react(em)}>{em}</button>
            ))}
            {post.like_count > 0 && <span style={{ fontSize: 12, color: "var(--text-3)" }}>{post.like_count} 반응</span>}
          </div>
        </div>
      </div>

      {/* Comments */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, marginTop: 16, padding: "24px 32px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
          댓글 <span style={{ color }}>{post.comment_count}</span>
        </h3>

        {post.comments.length === 0 ? (
          <p style={{ fontSize: 14, color: "var(--text-3)", textAlign: "center", padding: "20px 0" }}>첫 댓글을 남겨보세요 🙂</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {post.comments.map(c => (
              <CommentItem key={c.id} comment={c} me={me} depth={0}
                onReply={cid => { setReplyTo(cid); setTimeout(() => document.getElementById("board-comment-input")?.focus(), 0); }}
                onDelete={deleteComment} />
            ))}
          </div>
        )}

        {/* Comment input */}
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          {replyTo !== null && (
            <div style={{ fontSize: 12, color, marginBottom: 8, display: "flex", gap: 6 }}>
              <span>↩ 댓글 #{replyTo}에 답글</span>
              <button className="t-btn" style={{ fontSize: 11, padding: "0 6px" }} onClick={() => setReplyTo(null)}>취소</button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <input id="board-comment-input" className="t-input" style={{ flex: 1, fontSize: 13 }}
              value={commentText} placeholder="댓글을 입력하세요…"
              onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }} />
            <button className="t-btn t-btn-primary" style={{ fontSize: 13, padding: "0 20px", flexShrink: 0 }}
              onClick={addComment} disabled={!commentText.trim()}>등록</button>
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {showEdit && (
        <PostModal cats={cats} initial={post} onClose={() => setShowEdit(false)} onSave={load} />
      )}
    </div>
  );
}
